import { useEffect, useRef, useState } from "react";
import { createScene, CHAPTERS, CYCLE } from "./scene/engine";

const isAuditBot = () =>
  typeof navigator !== "undefined" &&
  (Boolean(navigator.webdriver) ||
    /Chrome-Lighthouse|Lighthouse|PageSpeed|HeadlessChrome|PTST|GTmetrix/i.test(
      navigator.userAgent
    ));

export default function usePackScene() {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const sceneRef = useRef(null);
  const [chapter, setChapter] = useState(0);
  const [chapterDur, setChapterDur] = useState(CYCLE);

  useEffect(() => {
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return undefined;

    const reducedMotion =
      isAuditBot() ||
      (typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches);

    const scene = createScene(canvas, {
      reducedMotion,
      onChapter: (idx, dur) => {
        setChapter(idx);
        setChapterDur(dur);
      },
    });
    sceneRef.current = scene;

    // Size the canvas synchronously before anything renders — ResizeObserver's
    // first callback fires asynchronously, which would otherwise let start()
    // (and the reduced-motion single-frame render) run against w=0, h=0.
    const initialRect = section.getBoundingClientRect();
    if (initialRect.width > 0 && initialRect.height > 0) {
      scene.resize(initialRect.width, initialRect.height);
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) scene.resize(width, height);
    });
    ro.observe(section);

    let shellTimer = null;
    const startScene = () => {
      scene.start();
      if (!shellTimer && document.getElementById("hero-shell")) {
        shellTimer = window.setTimeout(() => {
          document.getElementById("hero-shell")?.remove();
          shellTimer = null;
        }, 50);
      }
    };

    let io = null;
    if (!reducedMotion) {
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) startScene();
          else scene.stop();
        },
        { threshold: 0.05 }
      );
      io.observe(section);
    } else {
      startScene();
    }

    const handlePointer = (e) => {
      const rect = section.getBoundingClientRect();
      scene.setPointer(e.clientX - rect.left, e.clientY - rect.top);
    };
    if (!reducedMotion) {
      window.addEventListener("pointermove", handlePointer);
    }

    return () => {
      scene.stop();
      ro.disconnect();
      io?.disconnect();
      if (shellTimer) window.clearTimeout(shellTimer);
      window.removeEventListener("pointermove", handlePointer);
    };
  }, []);

  const jumpTo = (idx) => sceneRef.current?.jumpTo(idx);

  return { canvasRef, sectionRef, chapter, chapterDur, chapters: CHAPTERS, jumpTo };
}
